import React, { useState } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { sharedApi, SHARED_ORIGIN } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { formatNZD, phoneDigits } from "@/lib/format";
import { formatDealPrice } from "@/lib/dealPrice";
import { toast } from "sonner";
import {
  Loader2, Phone, MessageCircle, ExternalLink, MapPin, Clock, ShieldCheck,
  Sparkles, FileDown, CheckCircle2, Ticket, Mail, Store, Globe, Facebook, Twitter,
} from "lucide-react";
import { DealCard } from "@/components/deals/DealCard";

const API_ORIGIN = SHARED_ORIGIN;

/** "$" style price (deal endpoint may return price OR just price_label). */
function priceOf(d) {
  if (d.price_label) return d.price_label;
  if (d.price) return formatNZD(d.price);
  return "Contact for price";
}

/** "You save $X" computed from original_value − price. */
function saveText(d) {
  if (d.original_value && d.price && d.original_value > d.price) {
    return `You save ${formatNZD(d.original_value - d.price)}`;
  }
  return null;
}

export default function DealDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [claimed, setClaimed] = useState(null); // { voucher, already_claimed } after Grab
  // Iter-467 — GrabOne-parity: MULTI-select option picker (single OR
  // several options) with a live running total; chosen names are sent
  // as selected_options on the claim. Mirrors the app's
  // /classified-deals/[id] behavior.
  const [selectedOpts, setSelectedOpts] = useState(null); // Set of option names
  const [imgIdx, setImgIdx] = useState(0);

  const q = useQuery({
    queryKey: ["deal", id],
    queryFn: async () => (await sharedApi.get(`/classifieds/deals/${id}`)).data,
    enabled: !!id,
  });

  // "More deals you may like" rail (GrabOne parity).
  const moreQ = useQuery({
    queryKey: ["deals", "more", id],
    queryFn: async () =>
      (await sharedApi.get("/classifieds/deals", { params: { country: "NZ", limit: 9 } })).data.deals || [],
    staleTime: 2 * 60_000,
  });

  // Persistence: if the signed-in user already claimed this deal, hydrate the
  // claimed-state block so a page reload doesn't offer the Grab CTA again.
  const myVouchersQ = useQuery({
    queryKey: ["my-vouchers", "for-deal", id, user?.id],
    queryFn: async () => (await sharedApi.get("/classifieds/deal-vouchers/my")).data,
    enabled: !!user && !!id,
    staleTime: 30_000,
    retry: 0,
  });

  React.useEffect(() => {
    if (claimed || !myVouchersQ.data) return;
    const existing = (myVouchersQ.data.vouchers || []).find((v) => v.deal_id === id);
    if (existing) setClaimed({ voucher: existing, already_claimed: true });
  }, [myVouchersQ.data, claimed, id]);

  const claimMut = useMutation({
    mutationFn: async (selectedNames) =>
      (
        await sharedApi.post(
          `/classifieds/deals/${id}/claim`,
          selectedNames?.length ? { selected_options: selectedNames } : {},
        )
      ).data,
    onSuccess: (data) => {
      setClaimed(data);
      toast.success(data.already_claimed ? "You've already claimed this deal" : "Voucher grabbed! Check My vouchers.");
    },
    onError: (err) => {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Could not claim this deal";
      if (status === 401) {
        toast.error("Please sign in to grab this deal");
        nav(`/login?next=${encodeURIComponent(loc.pathname)}`);
        return;
      }
      toast.error(String(detail));
    },
  });

  const handleGrab = (selectedNames) => {
    if (!user) {
      nav(`/login?next=${encodeURIComponent(loc.pathname)}`);
      return;
    }
    claimMut.mutate(selectedNames);
  };

  if (q.isLoading) return <div className="max-w-3xl mx-auto py-20 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" /></div>;
  if (q.isError || !q.data) return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center">
      <h1 className="font-display text-3xl font-bold text-slate-900">Deal not found</h1>
      <Link to="/deals" className="mt-6 inline-block rounded-full bg-[color:var(--brand-blue)] text-white font-bold px-5 py-2.5">Back to deals</Link>
    </div>
  );

  const d = q.data;
  const save = saveText(d);
  const stockNote = d.max_vouchers ? `${d.max_vouchers} vouchers available` : null;
  const endDate = d.active_to ? new Date(d.active_to).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }) : null;
  const voucher = claimed?.voucher;
  const pdfHref = voucher?.pdf_url?.startsWith("http") ? voucher.pdf_url : voucher?.pdf_url ? `${API_ORIGIN}${voucher.pdf_url}` : null;

  // Option picker (Iter-467, multi-select): the selected options drive the
  // running total / value / savings in the sidebar.
  const opts = Array.isArray(d.deal_options) ? d.deal_options : [];
  const selected = selectedOpts ?? new Set(opts[0]?.name ? [opts[0].name] : []);
  const toggleOpt = (name) => {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    setSelectedOpts(next);
  };
  const selectedList = opts.filter((o) => selected.has(o.name));
  const optTotal = selectedList.reduce((sum, o) => sum + (Number(o.price) || 0), 0);
  const allHaveMrp = selectedList.length > 0 && selectedList.every((o) => o.mrp != null);
  const shownPrice = opts.length > 0 ? (selectedList.length ? optTotal : null) : d.price;
  const shownWas = opts.length > 0
    ? (allHaveMrp ? selectedList.reduce((sum, o) => sum + Number(o.mrp), 0) : null)
    : d.original_value;
  // Iter-473+ — the Value/Discount/You Save row ALWAYS renders when a
  // price exists; without an original value it falls back to 0% saved.
  const valueAmount = shownPrice != null ? (shownWas && shownWas > shownPrice ? shownWas : shownPrice) : null;
  const discountPct = shownPrice != null ? Math.round((1 - shownPrice / valueAmount) * 100) : null;
  const images = Array.isArray(d.images) ? d.images.filter(Boolean) : [];
  const mainImg = images[Math.min(imgIdx, Math.max(images.length - 1, 0))];
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const moreDeals = (moreQ.data || []).filter((x) => x.id !== id).slice(0, 4);

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo title={d.title} description={(d.description || "").slice(0, 160)} image={d.images?.[0]} type="product" />
      <Breadcrumbs items={[{ label: "Deals", to: "/deals" }, { label: d.title }]} />

      <div className="mt-6 grid lg:grid-cols-12 gap-8">
        {/* LEFT */}
        <div className="lg:col-span-8 space-y-6">
          {mainImg && (
            <div>
              <div className="relative">
                <img src={mainImg} alt={d.title} className="w-full rounded-2xl shadow-card object-cover aspect-[16/10]" data-testid="deal-main-image" />
                {d.bought_count > 0 && (
                  <div
                    className="absolute top-3 left-3 rounded-full bg-black/70 backdrop-blur text-white text-xs font-bold px-3 py-1.5"
                    data-testid="deal-bought-count"
                  >
                    {d.bought_count}+ bought
                  </div>
                )}
              </div>
              {images.length > 1 && (
                <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="deal-image-thumbs">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setImgIdx(i)}
                      data-testid={`deal-thumb-${i}`}
                      className={`shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-colors ${i === imgIdx ? "border-[color:var(--brand-orange)]" : "border-transparent opacity-80 hover:opacity-100"}`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <h1 className="font-display text-3xl md:text-4xl font-black text-slate-900">{d.title}</h1>
            {d.posted_by && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-sm text-slate-600" data-testid="deal-posted-by">
                <Store className="w-4 h-4 text-slate-400" /> {d.posted_by}
              </div>
            )}
            {d.website && (
              <div className="mt-2">
                <a
                  href={d.website}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="deal-website"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--brand-blue)] hover:underline"
                >
                  <Globe className="w-4 h-4" /> Visit merchant website
                </a>
              </div>
            )}
            {d.description_html ? (
              <div
                data-testid="deal-description-html"
                className="mt-4 text-slate-800 leading-relaxed rich-html"
                dangerouslySetInnerHTML={{ __html: d.description_html }}
              />
            ) : (
              d.description && (
                <p className="mt-4 text-slate-800 whitespace-pre-wrap leading-relaxed">{d.description}</p>
              )
            )}
          </div>

          {/* Deal options (v2) — purchase-options table (GrabOne-style) */}
          {Array.isArray(d.deal_options) && d.deal_options.length > 0 && (
            <section data-testid="deal-options-table">
              <h2 className="font-display text-xl font-bold text-slate-900">Purchase options</h2>
              <div className="mt-3 rounded-2xl bg-white shadow-card overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-widest">
                    <tr>
                      <th className="text-left px-4 py-2.5 font-bold">Option</th>
                      <th className="text-right px-4 py-2.5 font-bold">Was</th>
                      <th className="text-right px-4 py-2.5 font-bold">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.deal_options.map((o, i) => (
                      <tr
                        key={i}
                        data-testid={`deal-option-row-${i}`}
                        className={i > 0 ? "border-t border-subtle" : ""}
                      >
                        <td className="px-4 py-3 font-semibold text-slate-800">{o.name}</td>
                        <td className="px-4 py-3 text-right text-slate-400 line-through tabular-nums">
                          {o.mrp != null ? formatNZD(o.mrp) : "—"}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-[color:var(--brand-orange)] tabular-nums">
                          {o.price != null ? formatNZD(o.price) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Highlights — prefer rich HTML (v2), then legacy string[], then plain string mirror */}
          {(() => {
            const hasHtml = !!d.highlights_html;
            const hasArr = Array.isArray(d.highlights) && d.highlights.length > 0;
            const hasStr = !hasArr && typeof d.highlights === "string" && d.highlights.trim().length > 0;
            if (!hasHtml && !hasArr && !hasStr) return null;
            return (
              <section data-testid="deal-highlights">
                <h2 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[color:var(--brand-orange)]" /> Highlights
                </h2>
                {hasHtml ? (
                  <div
                    data-testid="deal-highlights-html"
                    className="mt-3 text-slate-800 rich-html"
                    dangerouslySetInnerHTML={{ __html: d.highlights_html }}
                  />
                ) : hasArr ? (
                  <ul className="mt-3 space-y-1.5">
                    {d.highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-800">
                        <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-500 shrink-0" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p
                    data-testid="deal-highlights-plain"
                    className="mt-3 text-slate-800 whitespace-pre-wrap leading-relaxed"
                  >
                    {d.highlights}
                  </p>
                )}
              </section>
            );
          })()}

          {/* Options */}
          {Array.isArray(d.options) && d.options.length > 0 && (
            <section data-testid="deal-options">
              <h2 className="font-display text-xl font-bold text-slate-900">Available options</h2>
              <ul className="mt-3 grid sm:grid-cols-2 gap-2">
                {d.options.map((o, i) => (
                  <li key={i} className="rounded-xl bg-slate-50 border border-soft px-3 py-2 text-sm text-slate-800">{o}</li>
                ))}
              </ul>
            </section>
          )}

          {/* Iter-472 — LOCATION section (GrabOne mirror): map embed +
              Google Maps directions link + opening hours panel. */}
          {(d.opening_hours_html || d.opening_hours || d.address) && (
            <section data-testid="deal-location-section">
              <h2 className="font-display text-xl font-bold text-slate-900 inline-flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[color:var(--brand-orange)]" /> Location
              </h2>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                {d.address && (
                  <div className="rounded-2xl bg-white shadow-card overflow-hidden" data-testid="deal-address">
                    <iframe
                      title="Deal location map"
                      data-testid="deal-location-map"
                      src={`https://maps.google.com/maps?q=${encodeURIComponent(d.address)}&z=15&output=embed`}
                      className="w-full h-44 border-0"
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                    <div className="p-4">
                      <div className="text-xs font-bold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5" /> Where to redeem
                      </div>
                      <div className="mt-1.5 text-sm text-slate-800">{d.address}</div>
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(d.address)}`}
                        target="_blank"
                        rel="noreferrer noopener"
                        data-testid="deal-directions-link"
                        className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white text-xs font-bold px-4 py-2 transition-colors"
                      >
                        <ExternalLink className="w-3.5 h-3.5" /> Get directions
                      </a>
                    </div>
                  </div>
                )}
                {(d.opening_hours_html || d.opening_hours) && (
                  <div className="rounded-2xl bg-white shadow-card p-4" data-testid="deal-hours">
                    <div className="text-xs font-bold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" /> Opening hours
                    </div>
                    {d.opening_hours_html ? (
                      <div
                        data-testid="deal-hours-html"
                        className="mt-1.5 text-sm text-slate-800 rich-html"
                        dangerouslySetInnerHTML={{ __html: d.opening_hours_html }}
                      />
                    ) : (
                      <div className="mt-1.5 text-sm text-slate-800 whitespace-pre-wrap">{d.opening_hours}</div>
                    )}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Fine print / Ad Conditions — accordion */}
          {(d.fine_print_html || d.fine_print) && (
            <details className="rounded-2xl bg-slate-50 border border-soft p-4" data-testid="deal-fine-print">
              <summary className="cursor-pointer font-display font-bold text-slate-800 text-sm inline-flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-slate-500" /> Fine print &amp; T&amp;Cs
              </summary>
              {d.fine_print_html ? (
                <div
                  data-testid="deal-fine-print-html"
                  className="mt-3 text-sm text-slate-700 rich-html"
                  dangerouslySetInnerHTML={{ __html: d.fine_print_html }}
                />
              ) : (
                <p className="mt-3 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{d.fine_print}</p>
              )}
            </details>
          )}
        </div>

        {/* RIGHT — sticky price + Grab */}
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-28 space-y-4">
            <div className="rounded-3xl bg-white shadow-card p-6">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Price</div>
              <div className="mt-1 flex items-baseline gap-3">
                {shownWas && shownPrice && shownWas > shownPrice && (
                  <div className="text-sm text-slate-400 line-through tabular-nums" data-testid="deal-original-value">
                    {formatNZD(shownWas)}
                  </div>
                )}
                <div className="font-display text-3xl font-black text-[color:var(--brand-blue)]" data-testid="deal-price">
                  {shownPrice ? formatNZD(shownPrice) : priceOf(d)}
                </div>
              </div>

              {/* Iter-467 — multi-select option picker with running total */}
              {opts.length > 0 && (
                <div className="mt-3 rounded-xl border border-soft divide-y divide-[color:var(--border-soft,#e2e8f0)]" data-testid="deal-option-picker">
                  {opts.map((o) => {
                    const slug = String(o.name).toLowerCase().replace(/[^a-z0-9]+/g, "-");
                    const checked = selected.has(o.name);
                    return (
                      <label
                        key={o.name}
                        data-testid={`deal-opt-${slug}`}
                        className={`flex items-center gap-2.5 px-3 py-2.5 text-sm cursor-pointer transition-colors ${checked ? "bg-[color:var(--brand-blue-wash)]" : "hover:bg-slate-50"}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleOpt(o.name)}
                          className="accent-[color:var(--brand-blue)]"
                        />
                        <span className="flex-1 font-semibold text-slate-800">{o.name}</span>
                        <span className="tabular-nums text-slate-700">
                          {o.mrp != null && <span className="mr-1.5 text-slate-400 line-through">{formatNZD(o.mrp)}</span>}
                          {o.price != null ? formatNZD(o.price) : ""}
                        </span>
                      </label>
                    );
                  })}
                  <div className="flex items-center justify-between px-3 py-2.5 text-sm bg-slate-50" data-testid="deal-option-total">
                    <span className="font-bold text-slate-800">
                      Total{selectedList.length > 0 ? ` — ${selectedList.length} option${selectedList.length > 1 ? "s" : ""}` : ""}
                    </span>
                    <span className="font-display font-black text-[color:var(--brand-blue)] tabular-nums">
                      {selectedList.length ? formatNZD(optTotal) : "Select an option"}
                    </span>
                  </div>
                </div>
              )}

              {/* Value / Discount / You Save — always rendered when a
                  price exists (0% fallback without an original value) */}
              {discountPct != null ? (
                <div className="mt-3 grid grid-cols-3 rounded-xl border border-soft divide-x divide-[color:var(--border-soft,#e2e8f0)] text-center" data-testid="deal-value-strip">
                  <div className="py-2">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Value</div>
                    <div className="text-sm font-bold text-slate-800 tabular-nums" data-testid="deal-value-amount">{formatNZD(valueAmount)}</div>
                  </div>
                  <div className="py-2">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Discount</div>
                    <div className="text-sm font-bold text-slate-800 tabular-nums" data-testid="deal-discount-pct">{discountPct}%</div>
                  </div>
                  <div className="py-2">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">You save</div>
                    <div className="text-sm font-bold text-emerald-600 tabular-nums" data-testid="deal-save-amount">{formatNZD(valueAmount - shownPrice)}</div>
                  </div>
                </div>
              ) : (
                save && (
                  <div className="mt-2 inline-block rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold px-3 py-1" data-testid="deal-save-chip">
                    {save}
                  </div>
                )
              )}

              <div className="mt-4 text-sm text-slate-600">
                {[d.city, d.region, "New Zealand"].filter(Boolean).join(", ")}
              </div>
              {(endDate || stockNote) && (
                <div className="mt-1 text-xs text-slate-500 space-y-0.5">
                  {endDate && <div>Ends {endDate}</div>}
                  {stockNote && <div data-testid="deal-stock-note">{stockNote}</div>}
                </div>
              )}

              {/* Grab CTA / claimed state */}
              <div className="mt-6 space-y-2.5">
                {voucher ? (
                  <>
                    {/* Fresh-claim email banner (spec §1.2) — only on `already_claimed:false` */}
                    {claimed && !claimed.already_claimed && (
                      <div
                        className="rounded-xl bg-[color:var(--brand-blue-wash)] border border-[color:var(--brand-blue)]/20 px-3 py-2.5 text-[13px] text-slate-700 leading-snug"
                        data-testid="deal-fresh-claim-email-banner"
                      >
                        <Mail className="w-4 h-4 inline mr-1.5 text-[color:var(--brand-blue)]" />
                        We&apos;ve emailed your voucher (PDF attached) — you can also download it below.
                      </div>
                    )}
                    <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-center" data-testid="deal-claimed-block">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">{claimed.already_claimed ? "You've already claimed this deal" : "Voucher ready"}</div>
                      <div className="mt-1 font-mono text-lg font-bold text-slate-900 tabular-nums" data-testid="deal-voucher-code">{voucher.code}</div>
                    </div>
                    {(() => {
                      const fmt = formatDealPrice(voucher);
                      return fmt.payLine ? (
                        <div className="text-center text-sm font-semibold text-slate-800" data-testid="deal-voucher-pay-line">{fmt.payLine}</div>
                      ) : null;
                    })()}
                    {pdfHref && (
                      <a
                        href={pdfHref}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="deal-voucher-pdf"
                        className="w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 font-semibold bg-slate-900 hover:bg-slate-800 text-white transition-colors"
                      >
                        <FileDown className="w-4 h-4" /> View PDF voucher
                      </a>
                    )}
                    <Link
                      to="/my/vouchers"
                      data-testid="deal-my-vouchers-link"
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 font-semibold bg-slate-100 hover:bg-slate-200 text-slate-800 transition-colors"
                    >
                      <Ticket className="w-4 h-4" /> View my vouchers
                    </Link>
                  </>
                ) : (
                  <>
                    {(() => {
                      // Pay line follows the SELECTED options' running total.
                      const fmt = formatDealPrice(
                        opts.length > 0 ? { ...d, price: shownPrice, original_value: shownWas } : d,
                      );
                      return fmt.payLine ? (
                        <div className="text-center text-sm font-semibold text-slate-800" data-testid="deal-pay-at-store">{fmt.payLine}</div>
                      ) : null;
                    })()}
                    <button
                      type="button"
                      onClick={() => handleGrab(selectedList.map((o) => o.name))}
                      disabled={claimMut.isPending || (opts.length > 0 && selectedList.length === 0)}
                      data-testid="deal-grab-btn"
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full py-3 font-bold text-white bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] shadow-card disabled:opacity-70 transition-colors"
                    >
                      {claimMut.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                      <Ticket className="w-4 h-4" /> Grab this deal
                    </button>
                  </>
                )}

                {d.contact_phone && (
                  <a
                    href={`tel:${phoneDigits(d.contact_phone)}`}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 font-semibold border border-soft text-slate-800 hover:bg-slate-50 transition-colors"
                    data-testid="deal-contact-phone"
                  >
                    <Phone className="w-4 h-4" /> {d.contact_phone}
                  </a>
                )}
                {d.whatsapp && (
                  <a
                    href={`https://wa.me/${phoneDigits(d.whatsapp)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 font-semibold text-white transition-colors"
                    style={{ backgroundColor: "var(--whatsapp)" }}
                    data-testid="deal-contact-whatsapp"
                  >
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                  </a>
                )}
                {d.cta_href && !voucher && (
                  <a
                    href={d.cta_href}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 font-semibold text-[color:var(--brand-blue)] bg-[color:var(--brand-blue-wash)] hover:bg-white border border-[color:var(--brand-blue)] transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" /> Open offer
                  </a>
                )}
              </div>
            </div>

            {/* Share this deal (GrabOne / legacy parity) */}
            <div className="rounded-3xl bg-white shadow-card p-5" data-testid="deal-share-card">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Share this deal</div>
              <div className="mt-3 flex items-center gap-2.5">
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank" rel="noreferrer" aria-label="Share on Facebook" data-testid="deal-share-facebook"
                  className="w-10 h-10 rounded-full grid place-items-center text-white bg-[#1877F2] hover:opacity-90 transition-opacity"
                >
                  <Facebook className="w-4.5 h-4.5" />
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(d.title)}`}
                  target="_blank" rel="noreferrer" aria-label="Share on X" data-testid="deal-share-x"
                  className="w-10 h-10 rounded-full grid place-items-center text-white bg-slate-900 hover:opacity-90 transition-opacity"
                >
                  <Twitter className="w-4.5 h-4.5" />
                </a>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`${d.title} ${shareUrl}`)}`}
                  target="_blank" rel="noreferrer" aria-label="Share on WhatsApp" data-testid="deal-share-whatsapp"
                  className="w-10 h-10 rounded-full grid place-items-center text-white hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: "var(--whatsapp)" }}
                >
                  <MessageCircle className="w-4.5 h-4.5" />
                </a>
                <a
                  href={`mailto:?subject=${encodeURIComponent(d.title)}&body=${encodeURIComponent(shareUrl)}`}
                  aria-label="Share by email" data-testid="deal-share-email"
                  className="w-10 h-10 rounded-full grid place-items-center text-white bg-[color:var(--brand-orange)] hover:opacity-90 transition-opacity"
                >
                  <Mail className="w-4.5 h-4.5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* More deals you may like (GrabOne parity) */}
      {moreDeals.length > 0 && (
        <section className="mt-14" data-testid="deal-more-deals">
          <h2 className="font-display text-2xl font-bold text-slate-900">More deals you may like</h2>
          <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {moreDeals.map((x) => (
              <DealCard key={x.id} deal={x} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
